<template>
  <div v-if="client" class="page-position is-marginless column is-12 columns">
    <div class="has-background-white column is-5">
      <section class="is-relative is-height-100p-60px is-overflow-y-active">
        <m-tiers
          :tiers="client"
          :errors="errors"
          :messagesErrors="messagesErrors"
          @setLogo="setLogoClient"
          @msgErrorClose="msgErrorClose"/>

        <!-- Gestion des contact -->
        <m-tiers-contact v-if="!isModif" />

        <!-- Gestion des pièces jointes -->
        <m-tiers-pj v-if="!isModif" />
      </section>

      <!-- Footer -->
      <div class="is-footer-child-content has-background-white column is-5">
        <div class="level mx-20 mb-10 mt-10">
          <div class="level-left"></div>
          <div class="level-right">
            <div class="level-item">
              <b-button type="is-white" @click="$emit('close')">
                <p class="is-size-14px has-text-default">
                  {{$t('general.btnAnnuler')}}
                </p>
              </b-button>
            </div>
            <div class="level-item">
              <b-button type="is-primary" @click="creerOuModifierClient">
                <p class="is-size-14px has-text-weight-bold">
                  {{ btnValider }}
                </p>
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="column has-background-transparent pointer"
      @click="$emit('close')">
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { createNamespacedHelpers } from 'vuex';
import * as config from '@/configuration/clientConf';
import * as types from '@/store/modules/client-mutation-type';
import * as typesContact from '@/store/modules/contacts-mutation-type';
import gestionErreur from '@/services/general/gestionErreur';
import generePJ from '@/services/piecesJointes/genererPiecesJointes';
import translator from '@/services/client/translator';
import validationGeneral from '@/services/validation/validation';
import { genererObjectErrorTiers, isInvalidClient } from '@/services/gestionPanneau/validationTiers';
import MTiers from '@/components/client/m_tiers.vue';
import MTiersPj from '@/components/client/m_tiers_pj.vue';
import MTiersContact from '@/components/client/m_tiers_contact.vue';

const Client = createNamespacedHelpers('client');
const Contacts = createNamespacedHelpers('contacts');
const Societe = createNamespacedHelpers('societe');

export default {
  components: {
    MTiers,
    MTiersPj,
    MTiersContact,
  },
  props: {
    typeClient: String,
    isModif:  Boolean,
  },
  data () {
    return {
      client: null,
      messagesErrors: [],
      errors: genererObjectErrorTiers(),
    };
  },
  computed: {
    ...Client.mapGetters({
      getClient: 'client',
    }),
    ...Client.mapState({
      piecesJointes: 'piecesJointes',
    }),
    ...Contacts.mapGetters({
      getContacts: 'contacts',
    }),
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    baseI18n() {
      return this.isModif ? 'modifierClient' : 'creerClient';
    },
    btnValider() {
      if (this.typeClient === config.ENSEIGNE) return this.$i18n.t(`${this.baseI18n}.btnValiderEnseigne`);
      if (this.typeClient === config.POINT_VENTE) return this.$i18n.t(`${this.baseI18n}.btnValiderPDV`);
      return this.$i18n.t(`${this.baseI18n}.btnValider`);
    },
  },
  methods: {
    ...Client.mapMutations({
      addLogo: types.SET_LOGO,
      setClient: types.SET_CLIENT,
      setPropietyClient: types.SET_PROPRIETY_CLIENT,
      resetChamps: types.VIDER_CHAMPS,
    }),
    ...Client.mapActions({
      actionsCreerClient: 'createClient',
      actionsModifierClient: 'updateClient',
    }),
    ...Contacts.mapMutations({
      videContacts: typesContact.VIDE_CONTACTS,
    }),
    ...Contacts.mapActions({
      actionSetContactsTiers: 'setContactsTiers',
    }),
    msgErrorClose() {
      this.messagesErrors = [];
      this.errors = genererObjectErrorTiers();
    },
    async setLogoClient(file) {
      const piecesJointes = await generePJ([{file, placeType: 'Logo'}]);
      this.addLogo(piecesJointes[0]);
    },
    async creerOuModifierClient() {
      if (isInvalidClient(this.client, this.errors)) {
        this.messagesErrors.push(this.$t('general.msgErrorChampObligatoire'));
        return;
      }
      const loadingComponent = this.$buefy.loading.open({ container: null });
      try {
        this.client.type = config.TYPE_CLIENT;
        this.client.societeId = this.societeActiveId;
        this.client.sousTypes = [config.SOUS_TYPE_AUCUN];
        this.setClient(cloneDeep(this.client));
        const funcAsync = this.isModif ? this.actionsModifierClient : this.actionsCreerClient;
        const response = await funcAsync();
        this.resetChamps();
        this.videContacts();
        if (this.isModif) {
          this.$emit('update');
        }
        if (this.$route.name === 'liste-client' && response) {
          this.$router.push({
            name: 'detail-client',
            params: { type: this.typeClient, id: response.data.num },
          });
        }
        if (this.$route.name === 'creer-point-de-vente' && response) {
          this.$emit('create', response.data);
        }
        this.$emit('close');
      } catch (error) {
        if (error.response && error.response.data && error.response.data.error) {
          const msg = [];
          validationGeneral(this.$i18n, error.response, msg, translator);
          if (msg.length) {
            this.messagesErrors = msg.map(el => this.$t(el.message, el.parametres));
            return;
          }
        }
        gestionErreur(`Erreur lors de l'enregistrement d'un client`, error);
        throw error;
      } finally {
        loadingComponent.close();
      }
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open({ container: null });
    try {
      this.client = cloneDeep(this.getClient);
      this.client.typologie = this.typeClient;
      if (this.client.id) {
        await this.actionSetContactsTiers({ id: this.client.id });
      }
    } catch (error) {
      gestionErreur('Erreur interne', error);
      throw error;
    } finally {
      loadingComponent.close();
    }
  }
};
</script>
